import React, { useEffect, useState, useMemo, useId, useCallback } from "react";
import {
  FiBook,
  FiCalendar,
  FiLayers,
  FiBell,
  FiStar,
  FiMessageCircle,
  FiCheckCircle,
  FiCircle,
  FiExternalLink,
  FiArrowDown,
  FiTrendingUp,
  FiClock,
  FiRefreshCw,
  FiZap,
  FiUser,
  FiTarget,
  FiActivity,
  FiSun,
  FiCoffee,
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiPause,
  FiChevronDown,
  FiChevronUp,
  FiShuffle,
  FiGlobe,
  FiCreditCard,
} from "react-icons/fi";
import axiosWrapper from "../../utils/AxiosWrapper";
import toast from "react-hot-toast";

const token = () => localStorage.getItem("userToken");

const formatShortDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatInr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const STU_GOALS_KEY = "student-dash-week-goals-v1";

const defaultWeekGoals = () => [
  { id: "1", title: "Review class notes", done: false },
  { id: "2", title: "Check notices & timetable", done: false },
  { id: "3", title: "One practice / revision block", done: false },
];

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const DAILY_QUOTES = [
  "Small progress each day adds up.",
  "Focus beats talent when talent doesn’t focus.",
  "Your future is built from today’s reps.",
  "Ask questions early — clarity saves time.",
  "Organize notes today; thank yourself tomorrow.",
  "Consistency beats last-minute panic — a little every day wins.",
  "Discipline today, distinction tomorrow 🎯",
  "Plan the week on Sunday, win the week by Friday.",
  "Teach someone else what you learned — it locks in memory.",
  "Sleep and revision both count as study.",
  "Small wins compound — log one every evening.",
  "Global campus, same rule: show up consistently.",
  "Skim the syllabus monthly — it keeps you oriented.",
  "Batch notifications; check the portal on a rhythm you own.",
  "Two backups for important notes beat one perfect folder.",
  "Ask early; answers get cheaper with time.",
];

const dailyQuoteIndex = () => {
  const d = new Date();
  const seed = d.getFullYear() * 372 + (d.getMonth() + 1) * 31 + d.getDate();
  return seed % DAILY_QUOTES.length;
};

const persistVisitStreak = () => {
  try {
    const todayStr = new Date().toDateString();
    const last = localStorage.getItem("studentDashVisitAt");
    let prevStreak = parseInt(
      localStorage.getItem("studentDashStreakCount") || "0",
      10
    );
    if (!Number.isFinite(prevStreak) || prevStreak < 0) prevStreak = 0;

    if (last === todayStr) {
      return Math.max(1, prevStreak || 1);
    }

    const y = new Date();
    y.setDate(y.getDate() - 1);
    const streak = last === y.toDateString() ? prevStreak + 1 : 1;

    localStorage.setItem("studentDashVisitAt", todayStr);
    localStorage.setItem("studentDashStreakCount", String(streak));
    return streak;
  } catch {
    return 1;
  }
};

const typeBadge = (type) => {
  const map = {
    notes: "bg-sky-100 text-sky-800 border-sky-200/80",
    assignment: "bg-amber-100 text-amber-900 border-amber-200/80",
    syllabus: "bg-emerald-100 text-emerald-900 border-emerald-200/80",
    other: "bg-slate-100 text-slate-700 border-slate-200/80",
  };
  return map[type] || map.other;
};

const QuickLink = ({ icon: Icon, label, onClick, accent, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    className="group relative flex flex-col items-stretch rounded-2xl p-4 text-left bg-white/95 backdrop-blur border border-white/60 shadow-soft hover:shadow-lift hover:-translate-y-1 hover:border-indigo-200/90 ring-0 hover:ring-2 hover:ring-indigo-400/20 transition-all duration-300 min-h-[112px] active:scale-[0.97] overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-indigo-500/0 before:via-transparent before:to-violet-500/0 hover:before:to-violet-500/[0.06] before:pointer-events-none before:transition-colors"
  >
    <span
      className={`relative w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md mb-3 ${accent} group-hover:scale-110 group-hover:shadow-lg transition-transform duration-300`}
    >
      <Icon className="w-[22px] h-[22px]" strokeWidth={2} />
    </span>
    <span className="text-[15px] font-semibold text-slate-900 leading-tight group-hover:text-indigo-700 transition-colors">
      {label}
    </span>
    {subtitle && (
      <span className="text-xs text-slate-500 mt-1.5 leading-snug">{subtitle}</span>
    )}
  </button>
);

const StatTile = ({ label, value, hint, accent, delay, onClick }) => (
  <div
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={(e) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    className={`rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/60 px-4 py-4 shadow-soft transition-all opacity-0 animate-fade-in-up text-left ${
      onClick
        ? "cursor-pointer hover:border-indigo-300 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.97] focus-visible:outline focus-visible:ring-2 focus-visible:ring-cyan-300"
        : "hover:border-indigo-200/60"
    }`}
    style={{ animationDelay: `${delay}ms`, animationFillMode: "forwards" }}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </p>
    <p className={`text-2xl font-bold tabular-nums mt-1 ${accent}`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const ProgressRing = ({ percent }) => {
  const rawId = useId().replace(/:/g, "");
  const size = 92;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  const gid = `${rawId}-pg`;

  return (
    <div className="relative w-[92px] h-[92px] shrink-0">
      <svg
        width={size}
        height={size}
        className="transform -rotate-90 drop-shadow-md"
        aria-hidden
      >
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
        <span className="text-xl font-bold text-white leading-none">{percent}</span>
        <span className="text-[9px] uppercase tracking-wider text-indigo-200 font-semibold mt-0.5">
          ready
        </span>
      </div>
    </div>
  );
};

const weekDaysAroundToday = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 3);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
};

const WeekStrip = () => {
  const days = useMemo(() => weekDaysAroundToday(), []);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIdx = useMemo(() => {
    const tn = new Date();
    tn.setHours(0, 0, 0, 0);
    const tMillis = tn.getTime();
    return days.findIndex((d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x.getTime() === tMillis;
    });
  }, [days]);
  const [picked, setPicked] = useState(() => (todayIdx >= 0 ? todayIdx : 3));

  const pickedNorm = days[picked]
    ? new Date(days[picked])
    : new Date(today);
  pickedNorm.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (pickedNorm.getTime() - today.getTime()) / 86400000
  );
  const pickerHint =
    diffDays === 0
      ? "Today — open timetable or jot one priority task."
      : diffDays > 0
        ? `${diffDays} day${diffDays === 1 ? "" : "s"} ahead · plan readings early.`
        : `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"} ago · review notes you covered.`;

  return (
    <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-4 sm:p-5 overflow-hidden">
      <div className="flex items-center gap-2 mb-3 text-slate-700">
        <FiSun className="w-5 h-5 text-amber-500 shrink-0" />
        <div>
          <h3 className="text-sm font-bold text-slate-900">This week</h3>
          <p className="text-xs text-slate-500">
            Tap a day · today stays highlighted in gradient
          </p>
        </div>
      </div>
      <div className="flex justify-between gap-1 sm:gap-2 overflow-x-auto no-scrollbar pt-1">
        {days.map((d, i) => {
          const d0 = new Date(d);
          d0.setHours(0, 0, 0, 0);
          const isToday = d0.getTime() === today.getTime();
          const isPicked = i === picked;
          return (
            <button
              key={d.toISOString()}
              type="button"
              aria-pressed={isPicked}
              onClick={() => setPicked(i)}
              className={`
                flex flex-col items-center min-w-[2.75rem] sm:min-w-[3.25rem] rounded-xl py-2 px-1 border transition-all outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
                ${isToday
                  ? "bg-gradient-to-b from-indigo-600 to-violet-600 text-white border-transparent shadow-md scale-105 ring-2 ring-white/70"
                  : isPicked
                    ? "bg-indigo-50 text-indigo-900 border-indigo-200 ring-1 ring-indigo-200 shadow-sm scale-[1.02]"
                    : "bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100"
                }
              `}
            >
              <span
                className={`text-[10px] uppercase font-bold ${isToday ? "text-indigo-100" : "text-slate-400"}`}
              >
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </span>
              <span className="text-lg font-bold tabular-nums leading-tight mt-0.5">
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-3 leading-snug border-t border-slate-100 pt-3">
        {pickerHint}
      </p>
    </div>
  );
};

const MiniSnapshotBars = ({ values, labels }) => {
  const max = Math.max(1, ...values);
  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-indigo-950 text-white p-4 sm:p-5 shadow-lift border border-white/10">
      <div className="flex items-center gap-2 mb-4">
        <FiActivity className="w-5 h-5 text-cyan-400 shrink-0" />
        <div>
          <h3 className="text-sm font-bold">Snapshot bars</h3>
          <p className="text-[11px] text-indigo-200/90">Relative activity across modules</p>
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 h-28 px-1">
        {values.map((v, i) => {
          const h = Math.round((v / max) * 100);
          return (
            <div
              key={labels[i]}
              className="flex-1 flex flex-col items-center gap-1.5 min-w-0"
            >
              <div className="w-full flex items-end justify-center h-20 rounded-t-lg bg-white/5 overflow-hidden">
                <div
                  className="w-[72%] max-w-[2rem] rounded-t-md bg-gradient-to-t from-cyan-500 to-indigo-400 transition-all duration-500"
                  style={{ height: `${Math.max(8, h)}%` }}
                />
              </div>
              <span className="text-[9px] sm:text-[10px] text-center text-indigo-100/80 font-medium truncate w-full">
                {labels[i]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StudentDashboard = ({
  profileData,
  navigate,
  refreshNonce = 0,
  onRefresh,
  bedPracticeSetCount = 0,
}) => {
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setLiveTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const localeNow = useMemo(() => {
    void liveTick;
    const d = new Date();
    const line = d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    let tz = "";
    try {
      tz = Intl.DateTimeFormat(undefined, {}).resolvedOptions().timeZone || "";
    } catch {
      /* noop */
    }
    return { line, tz: tz.replace(/_/g, " ") };
  }, [liveTick]);

  const [weekGoals, setWeekGoals] = useState(() => {
    try {
      const raw = localStorage.getItem(STU_GOALS_KEY);
      if (!raw) return defaultWeekGoals();
      const p = JSON.parse(raw);
      if (
        Array.isArray(p) &&
        p.length > 0 &&
        p.every((x) => x && typeof x.id === "string" && typeof x.title === "string")
      ) {
        return p.map((x) => ({
          id: x.id,
          title: x.title,
          done: Boolean(x.done),
        }));
      }
    } catch {
      /* noop */
    }
    return defaultWeekGoals();
  });

  const persistGoals = useCallback((next) => {
    setWeekGoals(next);
    try {
      localStorage.setItem(STU_GOALS_KEY, JSON.stringify(next));
    } catch {
      /* noop */
    }
  }, []);

  const [notices, setNotices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [exams, setExams] = useState([]);
  const [examCount, setExamCount] = useState(0);
  const [marksCount, setMarksCount] = useState(0);
  const [hasTimetable, setHasTimetable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visitStreak, setVisitStreak] = useState(() => persistVisitStreak());
  const [quoteIdx, setQuoteIdx] = useState(() => dailyQuoteIndex());

  /** Streak must re-run when the calendar day changes while this screen stays mounted (SPA / tab left open). */
  useEffect(() => {
    const sync = () => {
      try {
        setVisitStreak(persistVisitStreak());
      } catch {
        setVisitStreak(1);
      }
    };
    sync();
    const onVis = () => {
      if (!document.hidden) sync();
    };
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", onVis);
    const id = window.setInterval(sync, 60_000);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", onVis);
      window.clearInterval(id);
    };
  }, [refreshNonce]);
  const [feedFilter, setFeedFilter] = useState("all");
  const [expandedFeedId, setExpandedFeedId] = useState(null);
  const [focusEndMs, setFocusEndMs] = useState(null);
  const [focusTick, setFocusTick] = useState(0);
  const [spotlightIdx, setSpotlightIdx] = useState(0);

  const branchId = profileData?.branchId?._id || profileData?.branchId;
  const semester = profileData?.semester;

  const firstName = profileData?.firstName || "Student";
  const methodPaperName = profileData?.methodPaperSubSubject?.name;
  const mediaBase = process.env.REACT_APP_MEDIA_LINK || "";

  const go = (page) => navigate(`/student?page=${page}`);

  const goProfileTab = (t) => {
    navigate(
      `/student?page=profile${t ? `&tab=${encodeURIComponent(t)}` : ""}`
    );
  };

  const activeQuote = DAILY_QUOTES[quoteIdx % DAILY_QUOTES.length];

  const shuffleQuote = () => {
    setQuoteIdx((prev) => {
      if (DAILY_QUOTES.length < 2) return 0;
      let next = prev;
      while (next === prev) {
        next = Math.floor(Math.random() * DAILY_QUOTES.length);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!branchId || semester == null) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const auth = { Authorization: `Bearer ${token()}` };
      try {
        const requests = await Promise.allSettled([
          axiosWrapper.get(`/notice`, { headers: auth }),
          axiosWrapper.get(`/material?semester=${semester}&branch=${branchId}`, {
            headers: auth,
          }),
          axiosWrapper.get(`/exam?semester=${semester}`, { headers: auth }),
          axiosWrapper.get(`/marks/student?semester=${semester}`, {
            headers: auth,
          }),
          axiosWrapper.get(
            `/timetable?semester=${semester}&branch=${branchId}`,
            { headers: auth }
          ),
        ]);

        if (cancelled) return;

        const [nRes, mRes, eRes, mkRes, tRes] = requests;

        if (nRes.status === "fulfilled" && nRes.value.data?.success) {
          const list = Array.isArray(nRes.value.data.data)
            ? nRes.value.data.data
            : [];
          const forMe = list.filter(
            (x) => x.type === "student" || x.type === "both"
          );
          setNotices(forMe.slice(0, 15));
        } else setNotices([]);

        if (mRes.status === "fulfilled" && mRes.value.data?.success) {
          const list = Array.isArray(mRes.value.data.data)
            ? mRes.value.data.data
            : [];
          setMaterials(list);
        } else setMaterials([]);

        if (eRes.status === "fulfilled" && eRes.value.data?.success) {
          const list = Array.isArray(eRes.value.data.data)
            ? eRes.value.data.data
            : [];
          setExams(list);
          setExamCount(list.length);
        } else {
          setExams([]);
          setExamCount(0);
        }

        if (mkRes.status === "fulfilled" && mkRes.value.data?.success) {
          const list = Array.isArray(mkRes.value.data.data)
            ? mkRes.value.data.data
            : [];
          setMarksCount(list.length);
        } else setMarksCount(0);

        if (tRes.status === "fulfilled" && tRes.value.data?.success) {
          const rows = tRes.value.data.data;
          setHasTimetable(Boolean(Array.isArray(rows) && rows.length > 0));
        } else setHasTimetable(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [branchId, semester, refreshNonce]);

  useEffect(() => {
    if (!focusEndMs) return;
    const id = window.setInterval(() => setFocusTick((x) => x + 1), 400);
    return () => window.clearInterval(id);
  }, [focusEndMs]);

  const focusLeftSec = useMemo(() => {
    void focusTick;
    return focusEndMs
      ? Math.max(0, Math.ceil((focusEndMs - Date.now()) / 1000))
      : 0;
  }, [focusEndMs, focusTick]);

  useEffect(() => {
    if (!focusEndMs || focusLeftSec > 0) return;
    setFocusEndMs(null);
    toast.success("Focus round finished — stretch, hydrate, log what you did.");
  }, [focusEndMs, focusLeftSec]);

  useEffect(() => {
    if (!notices.length) {
      setSpotlightIdx(0);
      return;
    }
    setSpotlightIdx((i) => Math.min(i, notices.length - 1));
  }, [notices.length]);

  const upcomingExams = useMemo(() => {
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    return [...exams]
      .filter((e) => e?.date && new Date(e.date) >= startToday)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 4);
  }, [exams]);

  const pastExamsCount = Math.max(0, examCount - upcomingExams.length);

  const materialsPreview = materials.slice(0, 4);
  const materialCount = materials.length;

  const checklist = [
    {
      id: "timetable",
      label: "Class timetable published",
      done: hasTimetable,
      hint: hasTimetable ? "You're set" : "Check after admin upload",
      action: () => go("timetable"),
    },
    {
      id: "paper",
      label: "Method paper track selected",
      done: Boolean(methodPaperName),
      hint: methodPaperName
        ? "Saved — locked; office only for changes"
        : "Pick once in Profile → Settings (then locked)",
      action: () => goProfileTab("settings"),
    },
    {
      id: "marks",
      label: "Marks available this semester",
      done: marksCount > 0,
      hint:
        marksCount > 0
          ? `${marksCount} entr${marksCount === 1 ? "y" : "ies"}`
          : "Faculty will publish soon",
      action: () => go("marks"),
    },
    {
      id: "notice",
      label: "Catch up on notices",
      done: notices.length > 0,
      hint:
        notices.length > 0
          ? `${notices.length} recent for students`
          : "Nothing urgent",
      action: () => go("notice"),
    },
  ];

  const checklistDone = checklist.filter((c) => c.done).length;
  const setupPercent =
    checklist.length === 0
      ? 0
      : Math.round((checklistDone / checklist.length) * 100);

  const dashLevelLabel = useMemo(() => {
    const tier = Math.min(4, Math.floor(setupPercent / 25));
    const titles = ["Beginner", "Rising", "Solid", "Sharp", "All-star"];
    return titles[tier];
  }, [setupPercent]);

  const daysUntilNextExam = useMemo(() => {
    if (!upcomingExams.length || !upcomingExams[0]?.date) return null;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const e = new Date(upcomingExams[0].date);
    e.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((e - t) / 86400000));
  }, [upcomingExams]);

  const feeSummary = useMemo(() => {
    const cf = Number(profileData?.courseFee) || 0;
    const payments = Array.isArray(profileData?.feePayments)
      ? profileData.feePayments
      : [];
    const paid =
      typeof profileData?.totalFeePaid === "number"
        ? profileData.totalFeePaid
        : payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const due =
      typeof profileData?.feeBalanceDue === "number"
        ? profileData.feeBalanceDue
        : Math.max(0, cf - paid);
    return { courseFee: cf, paid, due, show: cf > 0 || paid > 0 };
  }, [profileData]);

  const activityFeed = useMemo(() => {
    const items = [];
    notices.forEach((n) => {
      if (!n.createdAt) return;
      items.push({
        kind: "notice",
        title: n.title,
        sub: "Notice",
        body: n.description ? String(n.description) : "",
        link: n.link || "",
        at: new Date(n.createdAt),
        id: `n-${n._id}`,
      });
    });
    materials.slice(0, 20).forEach((m) => {
      const raw = m.updatedAt || m.createdAt;
      if (!raw) return;
      const at = new Date(raw);
      if (Number.isNaN(at.getTime())) return;
      items.push({
        kind: "material",
        title: m.title,
        sub: `${m.type} · material`,
        body: "",
        link: "",
        file: m.file,
        at,
        id: `m-${m._id}`,
      });
    });
    items.sort((a, b) => b.at - a.at);
    return items.slice(0, 10);
  }, [notices, materials]);

  const filteredActivityFeed = useMemo(() => {
    if (feedFilter === "all") return activityFeed;
    return activityFeed.filter((x) =>
      feedFilter === "notice" ? x.kind === "notice" : x.kind === "material"
    );
  }, [activityFeed, feedFilter]);

  const tip = useMemo(() => {
    if (!methodPaperName) {
      return "Set your method paper sub-subject in your profile so faculty can align coursework with your track.";
    }
    if (upcomingExams.length > 0) {
      return `Next: ${upcomingExams[0].name} on ${formatShortDate(upcomingExams[0].date)} — review materials and timetable.`;
    }
    if (materialCount > 0) {
      return `${materialCount} material file${materialCount === 1 ? "" : "s"} ready — open Materials to download notes and syllabus.`;
    }
    return "Use Chat anytime to reach faculty or admin — we respond during college hours.";
  }, [methodPaperName, upcomingExams, materialCount]);

  const openMaterialFile = (file) => {
    if (!file || !mediaBase) return go("material");
    const url = `${mediaBase.replace(/\/$/, "")}/${String(file).replace(/^\//, "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-24 sm:pb-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-4 top-0 h-72 rounded-3xl bg-gradient-to-br from-indigo-500/15 via-violet-500/10 to-transparent blur-3xl opacity-90"
      />

      <div className="relative overflow-hidden rounded-[1.65rem] bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-lift ring-1 ring-white/10">
        <div
          aria-hidden
          className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-indigo-500/25 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-violet-500/20 blur-3xl"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:48px_48px] opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute left-[12%] top-16 w-28 h-28 rounded-[2rem] bg-gradient-to-br from-fuchsia-500/40 to-transparent opacity-60 blur-xl animate-float"
        />

        <div className="relative px-6 sm:px-10 py-8 sm:py-10">
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => typeof onRefresh === "function" && onRefresh()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 px-3 py-2 text-xs font-semibold text-white/95 disabled:opacity-50 transition-colors"
            >
              <FiRefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="max-w-xl space-y-4 opacity-0 animate-fade-in-up [animation-fill-mode:forwards]">
              <p className="text-indigo-200/95 text-xs sm:text-sm font-semibold uppercase tracking-[0.2em]">
                Student portal · {greeting()}
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Hello,{" "}
                <span className="bg-gradient-to-r from-white to-indigo-200 bg-clip-text text-transparent">
                  {firstName}
                </span>
              </h1>
              <p className="text-indigo-100/90 text-sm sm:text-base leading-relaxed flex gap-3">
                <FiTrendingUp className="w-5 h-5 shrink-0 text-amber-300/95 mt-0.5" />
                <span>{tip}</span>
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm border border-white/15 backdrop-blur-md">
                  <FiCalendar className="text-emerald-300 shrink-0" />
                  Semester{" "}
                  <strong className="text-white">{semester}</strong>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm border border-white/15 backdrop-blur-md max-w-full">
                  <span className="truncate">
                    {profileData?.branchId?.name || "Your branch"}
                  </span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-500/20 px-4 py-1.5 text-sm border border-orange-400/30 backdrop-blur-md">
                  <FiZap className="text-amber-300 shrink-0" />
                  <span>{visitStreak}-day streak</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-purple-500/15 px-4 py-1.5 text-sm border border-purple-400/25 backdrop-blur-md">
                  <FiStar className="text-purple-200 shrink-0" />
                  <span>Rank · {dashLevelLabel}</span>
                </span>
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-cyan-500/15 px-4 py-1.5 text-sm border border-cyan-400/25 backdrop-blur-md max-w-full"
                  title={localeNow.tz}
                >
                  <FiGlobe className="text-cyan-200 shrink-0" />
                  <span className="truncate font-medium tabular-nums">
                    {localeNow.line}
                  </span>
                </span>
                {localeNow.tz ? (
                  <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] text-indigo-200/95 border border-white/15 max-w-full truncate">
                    {localeNow.tz}
                  </span>
                ) : null}
                {loading && (
                  <span className="text-xs text-indigo-200/80 animate-pulse">
                    Updating summary…
                  </span>
                )}
              </div>

              {feeSummary.show && (
                <div className="rounded-2xl bg-white/10 border border-white/20 px-4 py-3 backdrop-blur-md mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-200/95 flex items-center gap-2">
                    <FiCreditCard className="w-4 h-4" aria-hidden />
                    Course fee status
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm text-white/95">
                    <span>
                      Total:{" "}
                      <strong className="tabular-nums">
                        {formatInr(feeSummary.courseFee)}
                      </strong>
                    </span>
                    <span className="text-emerald-200">
                      Paid:{" "}
                      <strong className="tabular-nums">
                        {formatInr(feeSummary.paid)}
                      </strong>
                    </span>
                    <span
                      className={
                        feeSummary.due > 0
                          ? "text-amber-200 font-semibold"
                          : "text-emerald-200 font-semibold"
                      }
                    >
                      Due:{" "}
                      <strong className="tabular-nums">
                        {formatInr(feeSummary.due)}
                      </strong>
                    </span>
                  </div>
                  {feeSummary.due > 0 && (
                    <p className="text-[11px] text-indigo-200/90 mt-2">
                      Pay the balance at the office as per schedule. This total
                      updates when admin records each instalment.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 min-w-[200px] lg:items-end opacity-0 animate-fade-in-up [animation-delay:120ms] [animation-fill-mode:forwards]">
              {methodPaperName ? (
                <div className="w-full lg:max-w-sm rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-5 py-4">
                  <p className="text-indigo-200 text-xs uppercase tracking-wide font-semibold">
                    Method paper
                  </p>
                  <p className="text-lg font-semibold text-white mt-1 leading-snug">
                    {methodPaperName}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => goProfileTab("settings")}
                  className="w-full lg:max-w-sm text-left rounded-2xl bg-amber-500/20 hover:bg-amber-500/30 backdrop-blur-md border border-amber-400/40 px-5 py-4 transition-colors"
                >
                  <p className="text-amber-100 text-xs uppercase tracking-wide font-semibold">
                    Action needed
                  </p>
                  <p className="text-white font-medium mt-1">
                    Choose method paper in Profile → Settings
                  </p>
                  <span className="inline-flex items-center gap-1 text-amber-200 text-sm mt-2">
                    Open settings <FiArrowDown className="w-4 h-4" />
                  </span>
                </button>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:justify-end">
                <ProgressRing percent={setupPercent} />
                <div className="text-center sm:text-left">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-200">
                    Portal readiness
                  </p>
                  <p className="text-lg font-bold text-white mt-1 flex items-center justify-center sm:justify-start gap-2">
                    <FiCheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                    <span>{checklistDone} of {checklist.length} checkpoints</span>
                  </p>
                  <p className="text-[11px] text-indigo-200/85 mt-1 max-w-[240px]">
                    Completing checkpoints fills the ring. Tap items in Your checklist below to jump
                    anywhere.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatTile
              delay={180}
              label="Materials"
              value={materialCount}
              hint="For your branch & sem"
              accent="text-violet-600"
              onClick={() => go("material")}
            />
            <StatTile
              delay={240}
              label="Exams listed"
              value={examCount}
              hint={
                upcomingExams.length
                  ? `${upcomingExams.length} upcoming`
                  : pastExamsCount
                    ? "Review past exams"
                    : "—"
              }
              accent="text-rose-600"
              onClick={() => go("exam")}
            />
            <StatTile
              delay={300}
              label="Marks rows"
              value={marksCount}
              hint={marksCount ? "View breakdown" : "Awaiting entry"}
              accent="text-sky-600"
              onClick={() => go("marks")}
            />
            <StatTile
              delay={360}
              label="Notices"
              value={notices.length}
              hint="Student & shared"
              accent="text-amber-600"
              onClick={() => go("notice")}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-start gap-4 rounded-2xl border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50/80 px-4 py-4 text-sm text-amber-950 shadow-sm ring-1 ring-amber-100/80">
        <div className="relative shrink-0">
          <FiZap className="w-6 h-6 text-amber-500 animate-shimmer relative z-10" />
          <span className="absolute inset-0 rounded-full bg-amber-400/25 blur-md" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <p className="font-semibold text-amber-900 text-xs uppercase tracking-wide">
              Today&apos;s line
            </p>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/85 border border-amber-100 text-amber-800 font-bold">
              Stay sharp ✨
            </span>
          </div>
          <p className="text-slate-800 leading-snug">{activeQuote}</p>
        </div>
        <button
          type="button"
          onClick={shuffleQuote}
          className="inline-flex items-center justify-center gap-2 self-start sm:self-center rounded-xl bg-white/90 px-4 py-2.5 text-xs font-bold text-amber-900 border border-amber-200 hover:bg-white transition-colors shadow-sm shrink-0"
        >
          <FiShuffle className="w-4 h-4" /> New quote
        </button>
      </div>

      <div className="mt-5 rounded-2xl border border-emerald-200/90 bg-gradient-to-br from-emerald-50/95 via-white to-cyan-50/70 p-4 sm:p-5 shadow-soft ring-1 ring-emerald-100/50">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <FiCheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            <h3 className="text-sm font-bold text-slate-900 tracking-tight">
              This week&apos;s focus · private on this device
            </h3>
          </div>
          <button
            type="button"
            onClick={() => persistGoals(defaultWeekGoals())}
            className="text-xs font-bold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
          >
            Reset list
          </button>
        </div>
        <ul className="space-y-2">
          {weekGoals.map((g, idx) => (
            <li
              key={g.id}
              className="flex items-start gap-3 rounded-xl bg-white/90 border border-emerald-100 px-3 py-2 shadow-sm"
            >
              <input
                type="checkbox"
                className="mt-1.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                checked={g.done}
                onChange={(e) => {
                  const next = weekGoals.map((x, i) =>
                    i === idx ? { ...x, done: e.target.checked } : x
                  );
                  persistGoals(next);
                }}
                aria-label={`Done: ${g.title}`}
              />
              <input
                className={`flex-1 min-w-0 text-sm bg-transparent border-0 p-0 focus:ring-0 font-medium placeholder:text-slate-400 ${
                  g.done ? "text-slate-400 line-through" : "text-slate-800"
                }`}
                value={g.title}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = weekGoals.map((x, i) =>
                    i === idx ? { ...x, title: v } : x
                  );
                  persistGoals(next);
                }}
                placeholder="Your goal…"
              />
            </li>
          ))}
        </ul>
        {bedPracticeSetCount > 0 ? (
          <button
            type="button"
            onClick={() => go("bed-practice")}
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white text-xs font-bold px-4 py-2.5 shadow-md hover:brightness-105 active:scale-[0.99] transition-all"
          >
            <FiZap className="w-4 h-4" /> Open quiz sets ({bedPracticeSetCount})
          </button>
        ) : null}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        <WeekStrip />
        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-4 sm:p-5 flex flex-col justify-between min-h-[140px] relative overflow-hidden">
          <span
            className="absolute top-3 right-3 w-20 h-20 rounded-full bg-rose-500/15 blur-xl pointer-events-none"
            aria-hidden
          />
          <div className="flex items-start gap-3 relative">
            <div className="w-11 h-11 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
              <FiTarget className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                Next exam countdown
              </h3>
              {upcomingExams[0] ? (
                <>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {upcomingExams[0].name}
                  </p>
                  <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-500 mt-2 tabular-nums">
                    {daysUntilNextExam === 0 ? "Today!" : `${daysUntilNextExam} days`}
                  </p>
                  <button
                    type="button"
                    onClick={() => go("exam")}
                    className="mt-2 text-xs font-bold text-indigo-600 hover:underline"
                  >
                    Detail & timetable →
                  </button>
                </>
              ) : (
                <p className="text-sm text-slate-600 mt-3 flex items-center gap-2">
                  <FiCoffee className="w-5 h-5 text-amber-500 shrink-0" />
                  No upcoming exam on file — open Materials or polish notes while you wait.
                </p>
              )}
            </div>
          </div>
        </div>
        <MiniSnapshotBars
          values={[materialCount, examCount, marksCount, notices.length]}
          labels={["Mats", "Exams", "Marks", "News"]}
        />
      </div>

      <div className="mt-5 rounded-2xl border border-indigo-100 bg-gradient-to-r from-white via-indigo-50/50 to-white p-4 shadow-soft">
        <div className="flex items-center gap-2 mb-3">
          <FiAward className="w-5 h-5 text-indigo-500" />
          <h3 className="text-sm font-bold text-slate-900 tracking-tight">
            Milestones (fun badges)
          </h3>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
          {[
            {
              unlock: hasTimetable,
              label: "Time master",
              sub: "Timetable",
              icon: FiCalendar,
            },
            {
              unlock: materialCount >= 5,
              label: "Resource fan",
              sub: `${materialCount} files`,
              icon: FiBook,
            },
            {
              unlock: marksCount > 0,
              label: "Result ready",
              sub: `${marksCount} marks rows`,
              icon: FiStar,
            },
            {
              unlock: Boolean(methodPaperName),
              label: "Track picked",
              sub: methodPaperName?.slice(0, 28) || "Paper",
              icon: FiTarget,
            },
            {
              unlock: bedPracticeSetCount > 0,
              label: "Quiz sets",
              sub:
                bedPracticeSetCount > 0
                  ? `${bedPracticeSetCount} practice set${bedPracticeSetCount === 1 ? "" : "s"}`
                  : "Open hub",
              icon: FiZap,
            },
            {
              unlock: true,
              label: "Connected",
              sub: "Chat on",
              icon: FiMessageCircle,
            },
          ].map((b) => {
            const BadgeIcon = b.icon;
            return (
            <button
              type="button"
              key={b.label}
              onClick={() =>
                b.label === "Connected"
                  ? go("chat")
                  : b.label === "Time master"
                    ? go("timetable")
                    : b.label === "Result ready"
                      ? go("marks")
                      : b.label === "Resource fan"
                        ? go("material")
                        : b.label === "Track picked"
                          ? goProfileTab("settings")
                          : b.label === "Quiz sets"
                            ? go("bed-practice")
                            : undefined
              }
              className={`
                shrink-0 flex flex-col items-center rounded-2xl border px-4 py-3 min-w-[118px]
                transition-transform hover:scale-[1.03] active:scale-[0.98]
                outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
                ${
                  b.unlock
                    ? "bg-white border-emerald-200 shadow-sm"
                    : "bg-slate-50 border-slate-200 opacity-65"
                }
              `}
            >
              <BadgeIcon
                className={`w-6 h-6 ${b.unlock ? "text-emerald-600" : "text-slate-400"}`}
              />
              <span className="text-xs font-bold text-slate-900 mt-2 text-center leading-tight">
                {b.label}
              </span>
              <span className="text-[10px] text-slate-500 text-center mt-0.5 line-clamp-2">
                {b.sub}
              </span>
            </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-stretch">
        <div className="rounded-2xl bg-gradient-to-br from-sky-950 via-indigo-950 to-slate-900 text-white p-5 sm:p-6 shadow-lift border border-white/10 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <FiTarget className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Focus mode</h3>
              <p className="text-xs text-indigo-200">
                Pomodoro-style timer keeps you in flow (runs in-browser only).
              </p>
            </div>
          </div>
          <div className="text-5xl font-mono font-bold tracking-tighter text-center py-6 tabular-nums">
            {focusEndMs
              ? `${String(Math.floor(focusLeftSec / 60)).padStart(2, "0")}:${String(focusLeftSec % 60).padStart(2, "0")}`
              : "--:--"}
          </div>
          <div className="flex flex-wrap gap-2 justify-center mb-4">
            {[
              { m: 25, label: "25 min" },
              { m: 15, label: "15 min" },
              { m: 5, label: "5 min sprint" },
            ].map(({ m, label }) => (
              <button
                key={label}
                type="button"
                disabled={Boolean(focusEndMs)}
                onClick={() =>
                  setFocusEndMs(Date.now() + m * 60 * 1000)
                }
                className="rounded-xl bg-white/15 hover:bg-white/25 px-4 py-2 text-xs font-bold border border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-3 justify-center mt-auto">
            {focusEndMs ? (
              <button
                type="button"
                onClick={() => setFocusEndMs(null)}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-500/90 hover:bg-rose-500 px-5 py-2.5 text-sm font-bold shadow-md"
              >
                <FiPause className="w-4 h-4" /> Clear timer
              </button>
            ) : (
              <span className="text-xs text-indigo-200 text-center px-4">
                Pick a length to begin. Pause anytime with Clear.
              </span>
            )}
          </div>
        </div>

        {notices.length > 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6 flex flex-col min-h-[200px]">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FiBell className="text-amber-500" /> Notice spotlight
              </h3>
              <span className="text-xs font-semibold text-slate-400">
                {spotlightIdx + 1} / {notices.length}
              </span>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 flex-1 flex flex-col">
              <p className="text-base font-semibold text-slate-900 leading-snug">
                {notices[spotlightIdx]?.title}
              </p>
              {notices[spotlightIdx]?.description && (
                <p className="text-sm text-slate-600 mt-3 line-clamp-4 leading-relaxed">
                  {notices[spotlightIdx].description}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-auto pt-4">
                <button
                  type="button"
                  onClick={() =>
                    setSpotlightIdx((i) =>
                      i <= 0 ? notices.length - 1 : i - 1
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
                >
                  <FiChevronLeft /> Prev
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setSpotlightIdx((i) =>
                      i >= notices.length - 1 ? 0 : i + 1
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
                >
                  Next <FiChevronRight />
                </button>
                {notices[spotlightIdx]?.link && (
                  <a
                    href={notices[spotlightIdx].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 ml-auto"
                  >
                    Link <FiExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center p-10 text-center text-slate-500 text-sm font-medium">
            Notice spotlight activates when admins publish announcements.
          </div>
        )}
      </div>

      {activityFeed.length > 0 && (
        <div className="mt-6 rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
            <div className="flex items-start gap-2">
              <FiActivity className="w-5 h-5 text-violet-500 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                  Live activity
                </h3>
                <p className="text-xs text-slate-500">
                  One blended timeline — tap a row for more detail where available.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0" role="group" aria-label="Filter timeline">
              {[
                { id: "all", label: "All" },
                { id: "notice", label: "Notices" },
                { id: "material", label: "Materials" },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setFeedFilter(f.id);
                    setExpandedFeedId(null);
                  }}
                  className={`rounded-xl px-3 py-2 text-xs font-bold border transition-colors ${
                    feedFilter === f.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:border-indigo-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {filteredActivityFeed.map((item) => (
              <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedFeedId((prev) =>
                      prev === item.id ? null : item.id
                    )
                  }
                  className="w-full flex gap-4 items-start text-left rounded-xl hover:bg-slate-50 px-2 -mx-2 py-1 transition-colors focus-visible:outline focus-visible:ring-2 focus-visible:ring-indigo-400"
                >
                  <span
                    className={`mt-1 w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      item.kind === "notice"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-violet-100 text-violet-700"
                    }`}
                  >
                    {item.kind === "notice" ? (
                      <FiBell className="w-4 h-4" />
                    ) : (
                      <FiBook className="w-4 h-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">
                        {item.title}
                      </p>
                      {expandedFeedId === item.id ? (
                        <FiChevronUp className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 capitalize">{item.sub}</p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {item.at.toLocaleString("en-IN", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                    {expandedFeedId === item.id && item.body ? (
                      <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap border-l-2 border-indigo-200 pl-3">
                        {item.body}
                      </p>
                    ) : null}
                    {expandedFeedId === item.id && item.kind === "notice" && item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-indigo-600 hover:underline"
                      >
                        Open linked resource <FiExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : null}
                    {expandedFeedId === item.id && item.kind === "material" && item.file ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMaterialFile(item.file);
                        }}
                        className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-indigo-600 hover:underline"
                      >
                        Open file <FiExternalLink className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4 px-1">
          Quick access
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3 sm:gap-4">
          <QuickLink
            icon={FiCalendar}
            label="Timetable"
            accent="bg-gradient-to-br from-emerald-500 to-teal-600"
            subtitle={hasTimetable ? "Available" : "Check soon"}
            onClick={() => go("timetable")}
          />
          <QuickLink
            icon={FiBook}
            label="Materials"
            accent="bg-gradient-to-br from-violet-500 to-purple-600"
            subtitle={materialCount ? `${materialCount} files` : "No files yet"}
            onClick={() => go("material")}
          />
          <QuickLink
            icon={FiBell}
            label="Notices"
            accent="bg-gradient-to-br from-amber-500 to-orange-600"
            subtitle="Campus updates"
            onClick={() => go("notice")}
          />
          <QuickLink
            icon={FiLayers}
            label="Exams"
            accent="bg-gradient-to-br from-rose-500 to-pink-600"
            subtitle={examCount ? `${examCount} records` : "—"}
            onClick={() => go("exam")}
          />
          <QuickLink
            icon={FiStar}
            label="Marks"
            accent="bg-gradient-to-br from-sky-500 to-blue-600"
            subtitle={marksCount ? `${marksCount} entries` : "Pending"}
            onClick={() => go("marks")}
          />
          <QuickLink
            icon={FiTarget}
            label="B.Ed practice"
            accent="bg-gradient-to-br from-fuchsia-500 to-indigo-600"
            subtitle={
              bedPracticeSetCount > 0
                ? `${bedPracticeSetCount} set${bedPracticeSetCount === 1 ? "" : "s"} · MCQ`
                : "Open quiz & leaderboard"
            }
            onClick={() => go("bed-practice")}
          />
          <QuickLink
            icon={FiMessageCircle}
            label="Chat"
            accent="bg-gradient-to-br from-cyan-500 to-indigo-600"
            subtitle="Faculty & admin"
            onClick={() => go("chat")}
          />
          <QuickLink
            icon={FiUser}
            label="Profile"
            accent="bg-gradient-to-br from-slate-600 to-slate-800"
            subtitle="Edit & settings"
            onClick={() => goProfileTab("overview")}
          />
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-5">
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Your checklist
              </h3>
              <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                {checklistDone}/{checklist.length}
              </span>
            </div>
            <ul className="space-y-3">
              {checklist.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={item.action}
                    className="w-full flex gap-3 text-left rounded-xl p-3 -m-1 hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100"
                  >
                    <span className="shrink-0 mt-0.5">
                      {item.done ? (
                        <FiCheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <FiCircle className="w-5 h-5 text-slate-300" />
                      )}
                    </span>
                    <span>
                      <span className="font-medium text-slate-800 block">
                        {item.label}
                      </span>
                      <span className="text-xs text-slate-500">{item.hint}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-5 sm:p-6 shadow-lift relative overflow-hidden">
            <div
              aria-hidden
              className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"
            />
            <h3 className="text-lg font-bold relative">Need help?</h3>
            <p className="text-sm text-indigo-100 mt-2 relative leading-relaxed">
              Message faculty or admin from Chat. Open Profile for account,
              contact details, and password.
            </p>
            <button
              type="button"
              onClick={() => go("chat")}
              className="mt-4 relative inline-flex items-center gap-2 rounded-xl bg-white text-indigo-700 font-semibold px-4 py-2.5 text-sm shadow-md hover:bg-indigo-50 transition-colors"
            >
              <FiMessageCircle className="w-4 h-4" />
              Open chat
            </button>
            <button
              type="button"
              onClick={() => goProfileTab("overview")}
              className="mt-2 relative block w-full text-center text-sm text-indigo-100 hover:text-white underline-offset-2 hover:underline"
            >
              Open profile & settings
            </button>
          </div>
        </div>

        <div className="lg:col-span-8 rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Upcoming exams
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Sorted by date · links open the published timetable
              </p>
            </div>
            <button
              type="button"
              onClick={() => go("exam")}
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 shrink-0"
            >
              View all →
            </button>
          </div>
          {upcomingExams.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 py-10 px-4 text-center">
              <FiClock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No upcoming exams</p>
              <p className="text-sm text-slate-500 mt-1">
                When admin adds exams for your semester, they will show here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {upcomingExams.map((exam) => (
                <li
                  key={exam._id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 first:pt-0 last:pb-0"
                >
                  <div>
                    <p className="font-semibold text-slate-900">{exam.name}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                      <span>{formatShortDate(exam.date)}</span>
                      <span className="capitalize text-indigo-600 font-medium">
                        {exam.examType === "mid" ? "Mid-term" : "End-term"}
                      </span>
                      <span>{exam.totalMarks} marks</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {exam.timetableLink && (
                      <a
                        href={exam.timetableLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors"
                      >
                        <FiExternalLink className="w-4 h-4" />
                        Timetable
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => go("material")}
                      className="inline-flex items-center rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-medium hover:bg-slate-800 transition-colors"
                    >
                      Study
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 mb-10 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-slate-900 tracking-tight">
              Latest notices
            </h3>
            <button
              type="button"
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
              onClick={() => go("notice")}
            >
              View all
            </button>
          </div>
          {notices.length === 0 ? (
            <p className="text-slate-500 text-sm mt-auto mb-auto py-8 text-center">
              You’re up to date — no notices for students right now.
            </p>
          ) : (
            <ul className="space-y-3 flex-1">
              {notices.map((n) => (
                <li
                  key={n._id}
                  className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 hover:border-indigo-100 transition-colors"
                >
                  <div className="flex justify-between gap-2 items-start">
                    <p className="font-semibold text-slate-900 leading-snug">
                      {n.title}
                    </p>
                    {n.link && (
                      <a
                        href={n.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50"
                        title="Open link"
                      >
                        <FiExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-2 leading-relaxed">
                    {n.description}
                  </p>
                  {n.createdAt && (
                    <p className="text-[11px] text-slate-400 mt-2">
                      {formatShortDate(n.createdAt)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-soft p-5 sm:p-6 flex flex-col min-h-[280px]">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-slate-900 tracking-tight">
              Recent materials
            </h3>
            <button
              type="button"
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
              onClick={() => go("material")}
            >
              Browse all
            </button>
          </div>
          {materialsPreview.length === 0 ? (
            <p className="text-slate-500 text-sm mt-auto mb-auto py-8 text-center">
              Materials will appear when faculty upload for your class.
            </p>
          ) : (
            <ul className="space-y-2 flex-1">
              {materialsPreview.map((m) => (
                <li
                  key={m._id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-slate-100 p-3 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">
                      {m.title}
                    </p>
                    <span
                      className={`inline-block mt-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md border capitalize ${typeBadge(m.type)}`}
                    >
                      {m.type}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => openMaterialFile(m.file)}
                    className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 py-1"
                  >
                    Open
                    <FiExternalLink className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <button
        type="button"
        aria-label="Open chat"
        onClick={() => go("chat")}
        className="fixed bottom-5 right-4 sm:bottom-7 sm:right-6 z-[45] flex items-center justify-center w-[3.65rem] h-[3.65rem] rounded-full shadow-lift border border-white/25 bg-gradient-to-br from-cyan-500 via-indigo-500 to-violet-600 text-white hover:scale-110 active:scale-95 transition-transform ring-4 ring-indigo-500/20"
      >
        <FiMessageCircle className="w-7 h-7" strokeWidth={2} />
      </button>
    </section>
  );
};

export default StudentDashboard;
